import { TheatreController, TheatreData } from "./useTheatreController";
import { SceneSessionChannel, SceneSessionClient } from "../backend";
import { useMemo, useRef, useState } from "@lukekaalim/act";
import { PhysicsDiceID, TheatreClient } from "../defs";
import { useAsync, useClientData, useJeffect, useLogger, useMappedClientData } from "@kosmod/kit";
import { Game, Player } from "@kosmod/sesame";
import { createCanvasUpdateCoordinator } from "@kosmod/stew";
import { GameAssetClient } from "@kosmod/assets";
import { K3D } from "@kosmod/graphics-3d";
import { Loadable } from "./useTheatreCoreResources";

export const useRemoteTheatreController = (
  game: Game,
  sClient: SceneSessionClient,
  self: Player,
  _: Player[],

  clients: {
    theatre: TheatreClient,
    assets: GameAssetClient,
  }
): [TheatreController, TheatreData, Loadable[]] => {
  const logger = useLogger('TheatreController');

  const chunks = sClient.subjects.chunks;

  const canvasCoordinator = useRef(() => createCanvasUpdateCoordinator(logger.createSubsystem('CanvasCoordinator'))).get();

  const minis = useMappedClientData(sClient.subjects.minis, map => [...map.values()], []);
  const canvases = useMappedClientData(sClient.subjects.canvases, map => [...map.values()], []);
  const players = useMappedClientData(sClient.subjects.players, map => [...map.values()], []);
  const surfaces = useMappedClientData(sClient.subjects.surfaces, map => [...map.values()], []);

  const defaultSurfaceId = useClientData(sClient.subjects.defaultSurfaceId);


  const allDice = useMappedClientData(sClient.subjects.dice, map => [...map.values()], [])
  const remotePhysicsDieTransforms = useMappedClientData(sClient.subjects.diceTransforms, map => new Map(map), new Map<PhysicsDiceID, K3D.Transform>());


  useJeffect(j => {
    j.add(sClient.subjects.onChunkChange.subscribe((changes) => {
      canvasCoordinator.queueMany(changes);
      canvasCoordinator.run();
    }))
    logger.log('setup coordinator')
  }, [sClient])

  const controller: TheatreController = {
    addMini(position, logic) {
      sClient.send(SceneSessionChannel.Actions.placeMini(
        position,
        logic
      ));
    },
    removeMini(miniId) {
      sClient.send({ type: 'remove-mini', miniId });
    },
    addCanvas(surface, brushes) {
      sClient.send(SceneSessionChannel.Actions.addCanvas(surface, brushes))
    },
    addPhysicsDie(diceTemplateId, initialTransform) {
      sClient.send({ type: 'add-die', diceTemplateId, initialTransform })
    },
    removePhysicsDie(physicsDieId) {
      sClient.send({ type: 'remove-die', physicsDieId })
    },
    moveMini(miniId, newPosition, animation) {
      const mini = sClient.subjects.minis.current.get(miniId)?.current;

      if (mini && mini.animation && animation) {
        const existingAnimation = mini.animation;
        if (existingAnimation.start + (existingAnimation.points.length * 400) > Date.now()) {
          logger.log(`Animation still going, extending`)

          sClient.send(SceneSessionChannel.Actions.moveMini(
            miniId,
            newPosition,
            { start: existingAnimation.start, points: [...existingAnimation.points, ...animation.points] }
          ));

          return;
        }
      }

      sClient.send(SceneSessionChannel.Actions.moveMini(
        miniId,
        newPosition,
        animation || { start: 0, points: [] }
      ));
    },
    setMiniLogic(miniId, logicIndex, logicState) {
      sClient.send(SceneSessionChannel.Actions.setMiniLogic(miniId, logicIndex, logicState))
    },
    
    paint(...strokes) {
      sClient.send({ type: 'paint-strokes', strokes })
    },
    
    sync(cursor, dice) {
      sClient.send({ type: 'sync', cursor, dice })
    },
    setTool(tool) {
      sClient.send({ type: 'set-player-tool', tool })
    },
    refresh() {
      setRefreshCount(r => r + 1)
    },
  }

  const [refreshCount, setRefreshCount] = useState(0);

  const { result: miniTemplateList, state: MINI_TEMPLATE_LOAD } = useAsync(() =>
    clients.theatre['mini/templates'].GET({ game: game.id, templateId: null }, null), [refreshCount])
  const { result: diceTemplateList, state: DICE_TEMPLATE_LOAD } = useAsync(() =>
    clients.theatre['dice/templates'].GET({ query: { game: game.id, templateId: null } }), [refreshCount])
  const { result: brushList, state: BRUSH_LOAD } = useAsync(() =>
    clients.theatre['terrain/brushes'].GET({ query: { game: game.id, brushId: null } }), [refreshCount])
  const { result: miniModelList, state: MINI_MODEL_LOAD } = useAsync(() =>
    clients.theatre['mini/models'].GET({ query: { game: game.id, miniModelId: null } }), [refreshCount])
  const { result: miniSpriteList, state: MINI_SPRITE_LOAD } = useAsync(() =>
    clients.theatre['mini/sprites'].GET({ query: { game: game.id, miniSpriteId: null } }), [refreshCount])

  const { miniTemplates, diceTemplates, brushes, miniModels, miniSprites } = useMemo(() => {
    return {
      miniTemplates: new Map(miniTemplateList && miniTemplateList.minis.map(m => [m.id, m]) || []),
      diceTemplates: new Map(diceTemplateList && diceTemplateList.body.dice.map(d => [d.id, d]) || []),
      miniModels: new Map(miniModelList && miniModelList.body.miniModels.map(m => [m.id, m]) || []),
      miniSprites: new Map(miniSpriteList && miniSpriteList.body.miniSprites.map(m => [m.id, m]) || []),
      brushes: new Map(brushList && brushList.body.brushes.map(b => [b.id, b]) || [])
    };
  }, [miniTemplateList, diceTemplateList, brushList, miniSpriteList, miniModelList])
  

  const { state: readyState } = useAsync(() => sClient.ready, [sClient.ready])

  const loadables: Loadable[] = [
    ['Mini Templates', MINI_TEMPLATE_LOAD === 'resolved'],
    ['Dice Templates', DICE_TEMPLATE_LOAD === 'resolved'],
    ['Terrain Brushes', BRUSH_LOAD === 'resolved'],
    ['Mini Model Definitions', MINI_MODEL_LOAD === 'resolved'],
    ['Mini Sprite Definitions', MINI_SPRITE_LOAD === 'resolved'],
  ]

  const data: TheatreData = {
    ready: readyState === 'resolved',
    contentReady: !loadables.some(l => !l[1]),
    players,
     
    minis,
    canvases,
    surfaces,
    chunks,
    defaultSurfaceId,
    
    canvasCoordinator,

    self,
    game,

    miniTemplates,
    diceTemplates,
    brushes,
    miniModels,
    miniSprites,

    ownedPhysicsDice: allDice.filter(d => d.simulator === self.id),
    remotePhysicsDice: allDice.filter(d => d.simulator !== self.id),
    remotePhysicsDieTransforms,
  }

  // logger.log(data)

  return [controller, data, loadables]
}